import { useEffect, useState } from "react";
import { Button, Table, Tooltip } from "flowbite-react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { HiArrowTrendingUp } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { AddEditExpansionOpportunity } from "./Modals";
import {
  useExpansionOpportunities,
  useDeleteConfirmation,
  useCurrency,
} from "../../../hooks";
import { TooltipContent, ScrollableTableWrapper } from "../../../components";
import {
  formatCurrency,
  getFormattedMoney,
  parseMoneyValue,
} from "../../../utils";

const COLUMNS = ["Name", "Type", "Amount", ""];

export const ExpansionFeatures = ({ customerData }) => {
  const { currency } = useCurrency();
  const {
    expansionOpportunities,
    fetchExpansionOpportunities,
    deleteExpansionOpportunity,
  } = useExpansionOpportunities();
  const { confirmDelete } = useDeleteConfirmation();
  const { t } = useTranslation();
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  useEffect(() => {
    fetchExpansionOpportunities(customerData.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (opportunityId) => {
    await confirmDelete(async () => {
      const deleted = await deleteExpansionOpportunity(opportunityId);
      if (deleted) {
        await fetchExpansionOpportunities(customerData.id);
      }
    }, "Are you sure you want to delete this expansion opportunity?");
  };

  const potentionMrr = expansionOpportunities.reduce(
    (acc, opp) => acc + parseMoneyValue(opp.additional_mrr).value,
    0,
  );

  return (
    <div>
      {/* Header outside card */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HiArrowTrendingUp className="size-5 text-emerald-500" />
          <h2 className="text-base font-semibold text-gray-900">
            Expansion features & account
          </h2>
          <Tooltip
            content={
              <TooltipContent
                title="About Feature & Account Expansion"
                size="lg"
                text={
                  <div>
                    Use this section to register opportunities related to the
                    customer's current contract or team. This includes:
                    <ul className="list-disc pl-5">
                      <li>Upsells for new features</li>
                      <li>Additional seats or licenses</li>
                      <li>Subscription package upgrades</li>
                    </ul>
                  </div>
                }
              />
            }
            // eslint-disable-next-line react/style-prop-object
            style="light"
          >
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-gray-400" />
          </Tooltip>
        </div>

        <AddEditExpansionOpportunity
          customerData={customerData}
          selectedOpportunity={selectedOpportunity}
          setSelectedOpportunity={setSelectedOpportunity}
          fetchExpansionOpportunities={fetchExpansionOpportunities}
        />
      </div>

      <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
        <div className="mb-4 flex items-center gap-6">
          <div>
            <p className="text-xs text-gray-500">Opportunities</p>
            <p className="text-2xl font-bold text-gray-900">
              {expansionOpportunities.length}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Potential MRR</p>
            <p
              className="text-2xl font-bold text-gray-900"
              title={formatCurrency(potentionMrr, currency, {
                abbreviated: false,
              })}
            >
              {formatCurrency(potentionMrr, currency)}
            </p>
          </div>
        </div>

        <div className="flex w-full flex-col">
          {expansionOpportunities.length > 0 ? (
            <ScrollableTableWrapper height="240px">
              <Table hoverable className="drop-shadow-none">
                <Table.Head>
                  {COLUMNS.map((column, index) => (
                    <Table.HeadCell key={column || `action-${index}`}>
                      {column}
                    </Table.HeadCell>
                  ))}
                </Table.Head>

                <Table.Body>
                  {expansionOpportunities.map(
                    ({ id, name, type, additional_mrr }) => {
                      return (
                        <Table.Row
                          key={id}
                          className="border-t dark:border-gray-700"
                        >
                          <Table.Cell>{name}</Table.Cell>
                          <Table.Cell>
                            {t(`common.customer.expansionOppTypes.${type}`)}
                          </Table.Cell>
                          <Table.Cell>
                            {getFormattedMoney(additional_mrr)}
                          </Table.Cell>
                          <Table.Cell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                color="gray"
                                size="sm"
                                onClick={() => {
                                  setSelectedOpportunity(
                                    expansionOpportunities.find(
                                      (opp) => opp.id === id,
                                    ),
                                  );
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                color="failure"
                                size="sm"
                                onClick={() => handleDelete(id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    },
                  )}
                </Table.Body>
              </Table>
            </ScrollableTableWrapper>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <HiArrowTrendingUp className="size-8 text-gray-300" />
              <div>
                <p className="text-sm font-medium text-gray-500">
                  No expansion opportunities yet
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Track upsells, seat expansions, and package upgrades.
                </p>
              </div>
              <AddEditExpansionOpportunity
                customerData={customerData}
                selectedOpportunity={selectedOpportunity}
                setSelectedOpportunity={setSelectedOpportunity}
                fetchExpansionOpportunities={fetchExpansionOpportunities}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
